import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Building2, Mail, Phone, MapPin, Calendar, Database, Edit, Ban, CheckCircle, LogIn, Activity } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const getAdminAuthHeader = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function RestaurantDetailPage() {
  const navigate = useNavigate();
  const { restaurantId } = useParams();
  const { impersonate } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [databaseInfo, setDatabaseInfo] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingDb, setLoadingDb] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);

  useEffect(() => {
    fetchRestaurantDetails();
    fetchDatabaseInfo();
    fetchActivityLogs();
  }, [restaurantId]);

  const fetchRestaurantDetails = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/restaurants/${restaurantId}`,
        { headers: getAdminAuthHeader() }
      );
      setRestaurant(response.data);
    } catch (error) {
      console.error('Error fetching restaurant:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load restaurant details');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDatabaseInfo = async () => {
    setLoadingDb(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/restaurants/${restaurantId}/database-info`,
        { headers: getAdminAuthHeader() }
      );
      setDatabaseInfo(response.data);
    } catch (error) {
      console.error('Error fetching database info:', error);
    } finally {
      setLoadingDb(false);
    }
  };

  const fetchActivityLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/restaurants/${restaurantId}/activity-logs?limit=10`,
        { headers: getAdminAuthHeader() }
      );
      setActivityLogs(response.data);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleLoginAsRestaurant = async () => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/restaurants/${restaurantId}/impersonate`,
        {},
        { headers: getAdminAuthHeader() }
      );
      
      // Use AuthContext impersonate method
      impersonate(response.data.access_token, response.data.user);
      
      // Show success and redirect
      toast.success(`Logging in as ${response.data.restaurant.name}...`);
      
      // Small delay then navigate
      setTimeout(() => {
        navigate('/dashboard');
      }, 500);
      
    } catch (error) {
      console.error('Error logging in as restaurant:', error);
      toast.error(error.response?.data?.detail || 'Failed to login as restaurant');
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = restaurant.status === 'active' ? 'suspended' : 'active';
    const action = newStatus === 'suspended' ? 'suspend' : 'activate';
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/restaurants/${restaurantId}`,
        { status: newStatus },
        { headers: getAdminAuthHeader() }
      );
      
      toast.success(`Restaurant ${action}d successfully`);
      fetchRestaurantDetails();
    } catch (error) {
      console.error(`Error ${action}ing restaurant:`, error);
      toast.error(error.response?.data?.detail || `Failed to ${action} restaurant`);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      active: { color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle },
      suspended: { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Ban },
      inactive: { color: 'bg-red-100 text-red-700 border-red-300', icon: Ban }
    };
    return configs[status] || configs.inactive;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading restaurant details...</p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Restaurant not found</p>
          <Button onClick={() => navigate('/admin/dashboard')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusBadge(restaurant.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Restaurant Details</h1>
                <p className="text-sm text-gray-600">View and manage restaurant information</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleToggleStatus}
                className={restaurant.status === 'active' ? 'text-yellow-600' : 'text-green-600'}
              >
                {restaurant.status === 'active' ? 'Suspend' : 'Activate'}
              </Button>
              <Button 
                onClick={handleLoginAsRestaurant}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <LogIn size={16} className="mr-2" />
                Login as Restaurant
              </Button>
              <Button 
                onClick={() => navigate(`/admin/restaurants/${restaurantId}/edit`)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Edit size={16} className="mr-2" />
                Edit Details
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Restaurant Info Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <Building2 size={24} className="text-red-600" />
                    </div>
                    {restaurant.name}
                  </CardTitle>
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border ${statusConfig.color}`}>
                    <StatusIcon size={16} />
                    {restaurant.status}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Restaurant ID</label>
                    <p className="text-sm font-mono bg-gray-100 px-3 py-2 rounded mt-1">{restaurant.id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Database Name</label>
                    <p className="text-sm font-mono bg-gray-100 px-3 py-2 rounded mt-1 flex items-center gap-2">
                      <Database size={14} />
                      {restaurant.database_name}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">Subdomain</label>
                  <p className="text-sm font-mono bg-blue-50 border border-blue-200 px-3 py-2 rounded mt-1">
                    {restaurant.subdomain}.waiterman.com
                  </p>
                </div>

                {restaurant.address && (
                  <div>
                    <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                      <MapPin size={14} />
                      Address
                    </label>
                    <p className="text-sm text-gray-700 mt-1">{restaurant.address}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Owner Information */}
            <Card>
              <CardHeader>
                <CardTitle>Owner / Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-600">Owner Name</label>
                  <p className="text-sm text-gray-900 font-semibold mt-1">{restaurant.owner_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Mail size={14} />
                    Email Address
                  </label>
                  <p className="text-sm text-gray-900 mt-1">{restaurant.owner_email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Phone size={14} />
                    Phone Number
                  </label>
                  <p className="text-sm text-gray-900 mt-1">{restaurant.owner_phone}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Commission Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Commission & Pricing</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-4">
                  <div className="text-5xl font-bold text-green-600 mb-2">
                    {restaurant.commission_rate}%
                  </div>
                  <p className="text-sm text-gray-600">Commission Rate</p>
                  <div className="mt-4 p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-green-800">
                      On $10,000/month billing:<br />
                      <span className="font-bold text-lg">${(10000 * restaurant.commission_rate / 100).toFixed(2)}</span> commission
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Database Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Database size={18} />
                  Database Mapping
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingDb ? (
                  <p className="text-sm text-gray-500">Loading...</p>
                ) : databaseInfo ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600">Database Name</label>
                      <p className="text-sm font-mono bg-blue-50 px-2 py-1 rounded mt-1 break-all">
                        {databaseInfo.database_name}
                      </p>
                    </div>
                    
                    <div>
                      <label className="text-xs font-medium text-gray-600">Status</label>
                      <p className="text-sm mt-1">
                        <span className="inline-flex items-center gap-1 text-green-600">
                          <CheckCircle size={14} />
                          {databaseInfo.status}
                        </span>
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-2 block">Collections & Records</label>
                      <div className="space-y-1">
                        {Object.entries(databaseInfo.collections).map(([collection, count]) => (
                          <div key={collection} className="flex justify-between text-xs bg-gray-50 px-2 py-1 rounded">
                            <span className="text-gray-700">{collection}</span>
                            <span className="font-semibold">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="pt-2 border-t">
                      <div className="flex justify-between text-sm font-semibold">
                        <span>Total Records:</span>
                        <span className="text-blue-600">{databaseInfo.total_records}</span>
                      </div>
                    </div>

                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={fetchDatabaseInfo}
                    >
                      Refresh Database Info
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No database info available</p>
                )}
              </CardContent>
            </Card>

            {/* Timestamps Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Timeline</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 flex items-center gap-2">
                    <Calendar size={12} />
                    Created At
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {new Date(restaurant.created_at).toLocaleString()}
                  </p>
                </div>
                {restaurant.updated_at && (
                  <div>
                    <label className="text-xs font-medium text-gray-600 flex items-center gap-2">
                      <Calendar size={12} />
                      Last Updated
                    </label>
                    <p className="text-sm text-gray-900 mt-1">
                      {new Date(restaurant.updated_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handleLoginAsRestaurant}
                >
                  <LogIn size={16} className="mr-2" />
                  Login as Restaurant
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/admin/restaurants/${restaurantId}/edit`)}
                >
                  <Edit size={16} className="mr-2" />
                  Edit Details
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handleToggleStatus}
                >
                  <Ban size={16} className="mr-2" />
                  {restaurant.status === 'active' ? 'Suspend Restaurant' : 'Activate Restaurant'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Activity Logs Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity size={20} />
              Recent Activity Logs
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingLogs ? (
              <p className="text-sm text-gray-500">Loading activity logs...</p>
            ) : activityLogs.length > 0 ? (
              <div className="space-y-3">
                {activityLogs.map((log, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-gray-900 capitalize">
                          {log.action.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600">
                        By: <span className="font-medium">{log.admin_email}</span>
                      </p>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                          {Object.entries(log.details).map(([key, value]) => (
                            <div key={key}>
                              <span className="font-medium">{key}:</span> {JSON.stringify(value)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Activity size={48} className="mx-auto mb-4 text-gray-300" />
                <p>No activity logs yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
